import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Archive, ArchiveRestore, ArrowLeft, Bell, Building2, CalendarDays, Check, History, Link2, ListTodo, LockKeyhole, MessageSquare, Paperclip, Pencil, Plus, Search, Send, Trash2, UserPlus, Users, X } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksService } from '../services/api';
import { getAvatarUrl } from './utils/Common';
import './css/TasksProjects.css';
import { TaskAttachmentList, TaskAttachmentPicker } from '../components/TaskAttachments';
import { CommentContent, MentionTextarea, serializeMentions } from './tasks-projects/mentions';
import TaskArchive from './tasks-projects/TaskArchive';

const columns = [
  { value: 'todo', label: 'Do zrobienia' },
  { value: 'in_progress', label: 'W trakcie' },
  { value: 'review', label: 'Do sprawdzenia' },
  { value: 'done', label: 'Zakończone' },
];
const statuses = Object.fromEntries(columns.map(column => [column.value, column.label]));
const priorities = { low: 'Niski', normal: 'Normalny', high: 'Wysoki', urgent: 'Pilny' };
const PAGE_SIZE = 30;
const taskSortTime = task => new Date(
  task.status === 'todo' ? task.created_at : (task.status_entered_at || task.created_at)
).getTime() || 0;
const sortColumnTasks = (a, b) => taskSortTime(b) - taskSortTime(a) || b.id - a.id;
const auditLabels = {
  organization_created: 'Utworzono projekt',
  organization_members_changed: 'Zmieniono członków projektu',
  organization_deleted: 'Usunięto projekt',
  task_created: 'Utworzono zadanie',
  task_updated: 'Edytowano zadanie',
  task_status_changed: 'Zmieniono status zadania',
  task_assignees_changed: 'Zmieniono przypisane osoby',
  task_watch_enabled: 'Włączono obserwowanie zadania',
  task_watch_disabled: 'Wyłączono obserwowanie zadania',
  task_comment_added: 'Dodano komentarz',
  task_comment_deleted: 'Usunięto komentarz',
  task_deleted: 'Usunięto zadanie',
  task_archived: 'Przeniesiono zadanie do archiwum',
  task_unarchived: 'Przywrócono zadanie z archiwum',
};
const errorMessage = error => typeof error.response?.data?.detail === 'string'
  ? error.response.data.detail : 'Nie udało się wykonać operacji. Spróbuj ponownie.';
const blankTask = status => ({ title: '', description: '', deadline: '', priority: 'normal', status, assignee_ids: [], checklist_items: [] });
const taskDraft = task => ({
  id: task.id, version: task.version, title: task.title, description: task.description, deadline: task.deadline || '',
  priority: task.priority, status: task.status, assignee_ids: task.assignees.map(person => person.id),
  attachments: task.attachments || [],
  comment_count: task.comment_count || 0,
  checklist_items: (task.checklist_items || []).map(item => ({ id: item.id, text: item.text, is_completed: item.is_completed })),
  archived_at: task.archived_at || null,
  archived_by: task.archived_by || null,
});
const assigneeDelta = (currentIds, nextIds) => ({
  add_ids: nextIds.filter(id => !currentIds.includes(id)),
  remove_ids: currentIds.filter(id => !nextIds.includes(id)),
});
const initials = name => name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
const auditDetails = entry => {
  if (entry.action === 'task_status_changed') {
    return `${statuses[entry.details.from] || entry.details.from} → ${statuses[entry.details.to] || entry.details.to}`;
  }
  if (['task_assignees_changed', 'organization_members_changed'].includes(entry.action)) {
    return `Dodano: ${entry.details.added_user_ids?.length || 0}, usunięto: ${entry.details.removed_user_ids?.length || 0}`;
  }
  if (entry.action === 'task_updated') return `Pola: ${(entry.details.changed_fields || []).join(', ')}`;
  if (['task_comment_added', 'task_comment_deleted'].includes(entry.action)) {
    const mentioned = entry.details.mentioned_user_ids?.length || 0;
    return `${entry.details.comment_preview || ''}${mentioned ? ` · oznaczone osoby: ${mentioned}` : ''}`;
  }
  return '';
};

function PersonAvatar({ person }) {
  const [imageFailed, setImageFailed] = useState(false);
  const url = getAvatarUrl(person.avatar);
  return <span className="tp-avatar" title={person.full_name} aria-label={person.full_name}>
    {url && !imageFailed ? <img src={url} alt="" onError={() => setImageFailed(true)} /> : initials(person.full_name)}
  </span>;
}

function AvatarStack({ people, limit = 4 }) {
  return <div className="tp-avatar-stack">
    {people.slice(0, limit).map(person => <PersonAvatar key={person.id} person={person} />)}
    {people.length > limit && <span className="tp-avatar tp-avatar-more">+{people.length - limit}</span>}
  </div>;
}

function OrganizationCard({ organization, userId, onOpen, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return <article className="tp-organization-card">
    <button className="tp-organization-open" onClick={() => onOpen(organization)}>
      <span className="tp-org-icon">{organization.is_personal ? <LockKeyhole /> : <Building2 />}</span>
      <span className="tp-org-copy"><strong>{organization.name}</strong><small>{organization.is_personal ? 'Projekt osobisty' : `${organization.members.length} członków`}</small></span>
      <AvatarStack people={organization.members} limit={3} />
    </button>
    {organization.owner_id === userId && <div className="tp-organization-controls">
      <button aria-label={`Opcje projektu ${organization.name}`} aria-expanded={menuOpen}
        onClick={() => setMenuOpen(value => !value)}>•••</button>
      {menuOpen && <div className="tp-organization-menu">
        <button onClick={() => { setMenuOpen(false); onDelete(organization); }}><Trash2 size={15} /> Usuń projekt</button>
      </div>}
    </div>}
  </article>;
}

function PeoplePicker({ people, selected, onChange, ownerId, lockedIds = [], disabled }) {
  return <div className="tp-people">{people.map(person => <label key={person.id}>
    <input type="checkbox" checked={selected.includes(person.id)} disabled={disabled || person.id === ownerId || lockedIds.includes(person.id)}
      onChange={event => onChange(event.target.checked ? [...selected, person.id] : selected.filter(id => id !== person.id))} />
    <PersonAvatar person={person} /> {person.full_name}{person.id === ownerId ? ' (właściciel)' : ''}
  </label>)}</div>;
}

function ChecklistEditor({ items, onChange, disabled }) {
  return <div className="tp-checklist-editor">
    {items.map((item, index) => <div key={item.id || `new-${index}`} className="tp-checklist-edit-row">
      <input type="checkbox" aria-label={`Oznacz element ${index + 1}`} checked={item.is_completed} disabled={disabled}
        onChange={event => onChange(items.map((current, itemIndex) => itemIndex === index ? { ...current, is_completed: event.target.checked } : current))} />
      <input required maxLength={500} placeholder="Treść elementu…" value={item.text} disabled={disabled}
        onChange={event => onChange(items.map((current, itemIndex) => itemIndex === index ? { ...current, text: event.target.value } : current))} />
      <button type="button" className="tp-checklist-remove" aria-label={`Usuń element ${index + 1}`} disabled={disabled}
        onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}><X size={16} /></button>
    </div>)}
    <button type="button" className="tp-add-checklist" disabled={disabled}
      onClick={() => onChange([...items, { id: null, text: '', is_completed: false }])}><Plus size={16} /> Dodaj element listy</button>
  </div>;
}

function TaskCard({ task, members, busy, copied, today, onOpen, onAssign, onCopyLink, onToggleWatch, onRequestDelete, onDragStart, onDragEnd }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [assigneesOpen, setAssigneesOpen] = useState(false);
  const assignmentRef = useRef(null);
  const overdue = task.deadline && task.deadline < today && task.status !== 'done';
  const checklist = task.checklist_items || [];
  const completedItems = checklist.filter(item => item.is_completed).length;

  useEffect(() => {
    if (!assigneesOpen) return undefined;
    const closeOutside = event => {
      if (!assignmentRef.current?.contains(event.target)) setAssigneesOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [assigneesOpen]);
  return <article className={`tp-task-card ${busy ? 'tp-task-moving' : ''}`} draggable={!busy && !menuOpen && !assigneesOpen}
    onDragStart={event => onDragStart(event, task)} onDragEnd={onDragEnd} onClick={() => onOpen(task)}>
    <div className="tp-task-topline">
      <span className="tp-task-identity"><span className="tp-task-id">#{task.id}</span>
        <button className={`tp-copy-link ${copied ? 'tp-copy-link-copied' : ''}`}
          aria-label={copied ? `Skopiowano link do zadania #${task.id}` : `Skopiuj link do zadania #${task.id}`}
          title={copied ? 'Link skopiowany' : 'Skopiuj link do zadania'} disabled={busy}
          onClick={event => { event.stopPropagation(); onCopyLink(task); }}>
          {copied ? <Check size={16} /> : <Link2 size={16} />}
        </button>
        <span className="tp-comment-count" title={`Komentarze: ${task.comment_count || 0}`} aria-label={`Liczba komentarzy: ${task.comment_count || 0}`}>
          <MessageSquare size={15} /> {task.comment_count || 0}
        </span>
        <span className="tp-attachment-count" title={`Załączniki: ${(task.attachments || []).length}`} aria-label={`Liczba załączników: ${(task.attachments || []).length}`}>
          <Paperclip size={15} /> {(task.attachments || []).length}
        </span>
      </span>
      <span className="tp-task-controls">
        <span className={`tp-priority tp-priority-${task.priority}`}>{priorities[task.priority]}</span>
        <button className={`tp-watch-task ${task.is_watched_by_me ? 'tp-watch-task-active' : ''}`}
          aria-label={`${task.is_watched_by_me ? 'Wyłącz' : 'Włącz'} powiadomienia o zadaniu #${task.id}`}
          aria-pressed={Boolean(task.is_watched_by_me)} title={task.is_watched_by_me ? 'Wyłącz powiadomienia' : 'Powiadamiaj o zmianach i komentarzach'}
          disabled={busy} onClick={event => { event.stopPropagation(); onToggleWatch(task); }}>
          <Bell size={16} fill={task.is_watched_by_me ? 'currentColor' : 'none'} />
        </button>
        <button className="tp-edit-task" title={`Opcje zadania #${task.id}`} aria-label={`Opcje zadania #${task.id}`}
          aria-expanded={menuOpen} disabled={busy} onClick={event => { event.stopPropagation(); setAssigneesOpen(false); setMenuOpen(value => !value); }}>•••</button>
        {menuOpen && <div className="tp-task-menu" onClick={event => event.stopPropagation()}>
          <button onClick={() => { setMenuOpen(false); onOpen(task); }}><Pencil size={15} /> Otwórz zadanie</button>
          <button onClick={() => { setMenuOpen(false); onCopyLink(task); }}><Link2 size={15} /> Kopiuj linka</button>
          <button className="tp-delete-option" onClick={() => { setMenuOpen(false); onRequestDelete(task); }}><Trash2 size={15} /> Usuń trwale</button>
        </div>}
      </span>
    </div>
    <h4>{task.title}</h4>
    {task.description && <p className="tp-description">{task.description}</p>}
    <div className="tp-task-footer">
      <span className={overdue ? 'tp-overdue' : ''}>
        {task.deadline && <><CalendarDays size={14} /> {task.deadline.split('-').reverse().join('.')}{overdue ? ' · Po terminie' : ''}</>}
      </span>
      {!!checklist.length && <span className="tp-checklist-progress"><ListTodo size={14} /> {completedItems}/{checklist.length}</span>}
    </div>
    <div ref={assignmentRef} className="tp-card-assignment" onClick={event => event.stopPropagation()}>
      <button className="tp-assign-trigger" aria-label={`Przypisz osoby do zadania #${task.id}`} aria-expanded={assigneesOpen}
        title="Przypisz osobę" disabled={busy} onClick={() => { setMenuOpen(false); setAssigneesOpen(value => !value); }}><UserPlus size={20} /></button>
      <AvatarStack people={task.assignees} />
      {assigneesOpen && <div className="tp-assignment-menu">
        <strong>Przypisz osoby</strong>
        {members.map(person => {
          const selected = task.assignees.some(assignee => assignee.id === person.id);
          return <label key={person.id}>
            <input type="checkbox" checked={selected} disabled={busy} onChange={event => {
              const ids = event.target.checked
                ? [...task.assignees.map(assignee => assignee.id), person.id]
                : task.assignees.filter(assignee => assignee.id !== person.id).map(assignee => assignee.id);
              onAssign(task, ids);
            }} />
            <PersonAvatar person={person} /><span>{person.full_name}</span>
          </label>;
        })}
      </div>}
    </div>
  </article>;
}

function Workspace({ organization, userId, onOrganizationChange, onAccessLost }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const archiveOpen = searchParams.get('view') === 'archive';
  const linkedTaskRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [editor, setEditor] = useState(null);
  const [editingExisting, setEditingExisting] = useState(false);
  const [editBase, setEditBase] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [drawerTab, setDrawerTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [commentContent, setCommentContent] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [commentMentions, setCommentMentions] = useState([]);
  const linkedTabRef = useRef(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [people, setPeople] = useState(null);
  const [memberIds, setMemberIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const membersRef = useRef(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [totals, setTotals] = useState(Object.fromEntries(columns.map(column => [column.value, 0])));
  const [loadingMore, setLoadingMore] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [copiedTaskId, setCopiedTaskId] = useState(null);
  const [reload, setReload] = useState(0);
  const [archiveReload, setArchiveReload] = useState(0);
  const [archiveTotal, setArchiveTotal] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setDrawerTab('details');
    setComments([]);
    setCommentTotal(0);
    setCommentsLoaded(false);
    setCommentsLoading(false);
    setCommentSubmitting(false);
    setDeletingCommentId(null);
    setCommentContent('');
    setCommentFiles([]);
    setCommentMentions([]);
    setConflict(null);
  }, [editor?.id]);

  useEffect(() => {
    // Links from mention notifications (?task=…&tab=comments) open straight on the comments.
    if (!editor?.id || editor.id !== linkedTaskRef.current || linkedTabRef.current !== 'comments') return;
    linkedTabRef.current = null;
    setDrawerTab('comments');
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor?.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all(columns.map(column => tasksService.tasks(organization.id, {
      status: column.value,
      search: debouncedSearch || undefined,
      assigned_to_me: onlyMine || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    }))).then(pages => {
      if (active) {
        setTasks(pages.flatMap(page => page.items));
        setTotals(Object.fromEntries(columns.map((column, index) => [column.value, pages[index].total])));
        setError('');
      }
    }).catch(error => {
      if (active) {
        setTasks([]);
        setTotals(Object.fromEntries(columns.map(column => [column.value, 0])));
        setError(errorMessage(error));
        if ([403, 404].includes(error.response?.status)) onAccessLost();
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [organization.id, reload, debouncedSearch, onlyMine, onAccessLost]);

  useEffect(() => {
    let active = true;
    tasksService.tasks(organization.id, { archived: true, limit: 1, offset: 0 }).then(page => {
      if (active) setArchiveTotal(page.total);
    }).catch(error => {
      if (!active) return;
      setArchiveTotal(0);
      if ([403, 404].includes(error.response?.status)) onAccessLost();
    });
    return () => { active = false; };
  }, [organization.id, archiveReload, onAccessLost]);

  useEffect(() => {
    const linkedTaskId = Number(searchParams.get('task'));
    if (!linkedTaskId || linkedTaskRef.current === linkedTaskId || loading) return undefined;
    const linkedTask = tasks.find(task => task.id === linkedTaskId);
    if (linkedTask) {
      linkedTaskRef.current = linkedTaskId;
      linkedTabRef.current = searchParams.get('tab');
      setEditor(taskDraft(linkedTask));
      setEditingExisting(false);
      return undefined;
    }
    let active = true;
    tasksService.task(organization.id, linkedTaskId).then(task => {
      if (!active) return;
      linkedTaskRef.current = linkedTaskId;
      linkedTabRef.current = searchParams.get('tab');
      setEditor(taskDraft(task));
      setEditingExisting(false);
    }).catch(error => {
      if (active) setError(error.response?.status === 404 ? 'Nie znaleziono wskazanego zadania.' : errorMessage(error));
    });
    return () => { active = false; };
  }, [searchParams, tasks, loading, organization.id]);

  async function perform(action) {
    setBusy(true); setError('');
    try { await action(); } catch (error) {
      setError(errorMessage(error));
      if ([403, 404].includes(error.response?.status)) { setTasks([]); setEditor(null); onAccessLost(); }
    } finally { setBusy(false); }
  }

  const matchesCurrentFilters = task => {
    const assigneeIds = task.assignees?.map(person => person.id) || task.assignee_ids || [];
    const matchesMine = !onlyMine || assigneeIds.includes(userId);
    const text = `${task.title || ''} ${task.description || ''}`.toLocaleLowerCase('pl');
    return matchesMine && text.includes(debouncedSearch.toLocaleLowerCase('pl'));
  };

  const storeSavedTask = (saved, original, isNew = false) => {
    const wasVisible = original ? matchesCurrentFilters(original) : false;
    const isVisible = matchesCurrentFilters(saved);
    setTasks(current => {
      const index = current.findIndex(task => task.id === saved.id);
      const withoutSaved = current.filter(task => task.id !== saved.id);
      if (!isVisible) return withoutSaved;
      if (index >= 0) {
        const next = [...current];
        next[index] = saved;
        return next;
      }
      return isNew ? [saved, ...withoutSaved] : withoutSaved;
    });
    setTotals(current => {
      const next = { ...current };
      if (isNew && isVisible) next[saved.status] += 1;
      if (original && wasVisible && (!isVisible || original.status !== saved.status)) {
        next[original.status] = Math.max(0, next[original.status] - 1);
      }
      if (original && isVisible && (!wasVisible || original.status !== saved.status)) next[saved.status] += 1;
      return next;
    });
  };

  const loadMore = async taskStatus => {
    const offset = tasks.filter(task => task.status === taskStatus).length;
    setLoadingMore(taskStatus); setError('');
    try {
      const page = await tasksService.tasks(organization.id, {
        status: taskStatus,
        search: debouncedSearch || undefined,
        assigned_to_me: onlyMine || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setTasks(current => {
        const ids = new Set(current.map(task => task.id));
        return [...current, ...page.items.filter(task => !ids.has(task.id))];
      });
      setTotals(current => ({ ...current, [taskStatus]: page.total }));
    } catch (error) {
      setError(errorMessage(error));
      if ([403, 404].includes(error.response?.status)) onAccessLost();
    } finally { setLoadingMore(null); }
  };

  const loadAudit = async (offset = 0) => {
    setAuditLoading(true); setError('');
    try {
      const page = await tasksService.audit(organization.id, { limit: 50, offset });
      setAuditItems(current => offset ? [...current, ...page.items] : page.items);
      setAuditTotal(page.total);
      setAuditOpen(true);
    } catch (error) {
      setError(errorMessage(error));
    } finally { setAuditLoading(false); }
  };

  const loadComments = async (offset = 0) => {
    if (!editor?.id) return;
    setCommentsLoading(true); setError('');
    try {
      const page = await tasksService.comments(organization.id, editor.id, { limit: 50, offset });
      setComments(current => offset ? [...current, ...page.items] : page.items);
      setCommentTotal(page.total);
      if (!offset) {
        setEditor(current => current ? { ...current, comment_count: page.total } : current);
        setTasks(current => current.map(task => task.id === editor.id
          ? { ...task, comment_count: page.total }
          : task));
      }
      setCommentsLoaded(true);
    } catch (error) {
      setError(errorMessage(error));
      if ([403, 404].includes(error.response?.status)) onAccessLost();
    } finally { setCommentsLoading(false); }
  };

  const openComments = () => {
    setDrawerTab('comments');
    if (!commentsLoaded && !commentsLoading) loadComments();
  };

  const addComment = async event => {
    event.preventDefault();
    const content = serializeMentions(commentContent, commentMentions).trim();
    if (!content || content.length > 5000 || commentSubmitting) return;
    setCommentSubmitting(true); setError('');
    try {
      const comment = await tasksService.addComment(organization.id, editor.id, content, commentFiles);
      setComments(current => [comment, ...current]);
      setCommentTotal(current => current + 1);
      setEditor(current => current ? { ...current, comment_count: (current.comment_count || 0) + 1 } : current);
      setTasks(current => current.map(task => task.id === editor.id
        ? { ...task, comment_count: (task.comment_count || 0) + 1 }
        : task));
      setCommentsLoaded(true);
      setCommentContent('');
      setCommentFiles([]);
      setCommentMentions([]);
    } catch (error) {
      setError(errorMessage(error));
      if ([403, 404].includes(error.response?.status)) onAccessLost();
    } finally { setCommentSubmitting(false); }
  };

  const deleteComment = async comment => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten komentarz wraz z załącznikami?')) return;
    setDeletingCommentId(comment.id); setError('');
    try {
      await tasksService.deleteComment(organization.id, editor.id, comment.id);
      setComments(current => current.filter(item => item.id !== comment.id));
      setCommentTotal(current => Math.max(0, current - 1));
      setEditor(current => current
        ? { ...current, comment_count: Math.max(0, (current.comment_count || 0) - 1) }
        : current);
      setTasks(current => current.map(task => task.id === editor.id
        ? { ...task, comment_count: Math.max(0, (task.comment_count || 0) - 1) }
        : task));
    } catch (error) {
      setError(errorMessage(error));
    } finally { setDeletingCommentId(null); }
  };

  const openEditor = task => {
    setPeople(null);
    setDeleteCandidate(null);
    setEditingExisting(false);
    setEditor(task ? taskDraft(task) : blankTask('todo'));
  };

  const openMembers = () => {
    if (people) {
      setPeople(null);
      setMemberSearch('');
      return;
    }
    perform(async () => {
      const candidates = await tasksService.candidates(organization.id);
      const merged = new Map([...organization.members, ...candidates].map(person => [person.id, person]));
      setPeople([...merged.values()]);
      setMemberIds(organization.members.map(person => person.id));
      setMemberSearch('');
      setEditor(null);
    });
  };

  // Archived tasks are not on the board: drop the card and its count.
  const removeFromBoard = task => {
    const original = tasks.find(item => item.id === task.id) || task;
    setTasks(current => current.filter(item => item.id !== task.id));
    if (!original.archived_at && matchesCurrentFilters(original)) {
      setTotals(current => ({ ...current, [original.status]: Math.max(0, current[original.status] - 1) }));
    }
  };

  const loadConflictingTask = async (taskId, original) => {
    try {
      const latest = await tasksService.task(organization.id, taskId);
      if (latest.archived_at) {
        // Someone archived the task meanwhile: show it read-only instead of offering an overwrite.
        removeFromBoard(original || { ...latest, archived_at: null });
        setEditor(taskDraft(latest));
        setEditingExisting(false);
        setArchiveReload(value => value + 1);
        return;
      }
      storeSavedTask(latest, original);
      setConflict(latest);
    } catch {
      // The conflict message is shown anyway; the user can refresh the board.
    }
  };

  const submitTask = draft => perform(async () => {
    const { id, files = [], version, ...data } = draft;
    delete data.attachments;
    delete data.comment_count;
    delete data.archived_at;
    delete data.archived_by;
    data.deadline = data.deadline || null;
    const original = id ? tasks.find(task => task.id === id) || null : null;
    let saved;
    try {
      saved = id
        ? await tasksService.updateTask(organization.id, id, { ...data, version }, files)
        : await tasksService.createTask(organization.id, data, files);
    } catch (error) {
      if (id && error.response?.status === 409) await loadConflictingTask(id, original);
      throw error;
    }
    storeSavedTask(saved, original, !id);
    setConflict(null);
    if (id) {
      setEditor(taskDraft(saved));
      setEditingExisting(false);
    } else {
      setEditor(null);
    }
  });

  const saveTask = event => {
    event.preventDefault();
    submitTask(editor);
  };

  const startExistingEdit = () => {
    setEditBase(editor);
    setConflict(null);
    setEditingExisting(true);
  };

  const cancelExistingEdit = () => {
    const savedTask = conflict || tasks.find(task => task.id === editor.id);
    setEditor(savedTask ? taskDraft(savedTask) : (editBase || editor));
    setConflict(null);
    setEditingExisting(false);
  };

  useEffect(() => {
    if (!editor && !deleteCandidate && !auditOpen && !people) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape' && !busy && !commentSubmitting && deletingCommentId === null) {
        if (deleteCandidate) setDeleteCandidate(null);
        else if (auditOpen) setAuditOpen(false);
        else if (editor) { setEditor(null); setEditingExisting(false); }
        else { setPeople(null); setMemberSearch(''); }
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [editor, deleteCandidate, auditOpen, people, busy, commentSubmitting, deletingCommentId]);

  useEffect(() => {
    if (!people) return undefined;
    const closeOutside = event => {
      if (!membersRef.current?.contains(event.target)) {
        setPeople(null);
        setMemberSearch('');
      }
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [people]);

  const deleteTask = () => perform(async () => {
    const deletedTask = deleteCandidate;
    await tasksService.deleteTask(organization.id, deleteCandidate.id);
    setTasks(current => current.filter(task => task.id !== deleteCandidate.id));
    if (matchesCurrentFilters(deletedTask)) {
      setTotals(current => ({ ...current, [deletedTask.status]: Math.max(0, current[deletedTask.status] - 1) }));
    }
    if (editor?.id === deleteCandidate.id) { setEditor(null); setEditingExisting(false); }
    setDeleteCandidate(null);
  });

  const applyDrawerTask = saved => {
    storeSavedTask(saved, tasks.find(item => item.id === saved.id) || null);
    setEditor(taskDraft(saved));
  };

  const toggleDrawerChecklistItem = item => perform(async () => {
    applyDrawerTask(await tasksService.updateChecklistItem(organization.id, editor.id, item.id, !item.is_completed));
  });

  const updateDrawerAssignees = assigneeIds => perform(async () => {
    applyDrawerTask(await tasksService.updateTaskAssignees(
      organization.id, editor.id, assigneeDelta(editor.assignee_ids, assigneeIds),
    ));
  });

  const setTaskArchived = (task, archived) => perform(async () => {
    const saved = await tasksService.setArchived(organization.id, task.id, archived);
    if (archived) removeFromBoard({ ...task, archived_at: null });
    else storeSavedTask(saved, null, true);
    setArchiveTotal(current => Math.max(0, current + (archived ? 1 : -1)));
    if (editor?.id === saved.id) {
      setEditor(taskDraft(saved));
      setEditingExisting(false);
    }
    setArchiveReload(value => value + 1);
  });

  const toggleArchiveView = () => {
    setEditor(null);
    setEditingExisting(false);
    setSearchParams(archiveOpen ? {} : { view: 'archive' });
  };

  const updateCardAssignees = (task, assigneeIds) => perform(async () => {
    const saved = await tasksService.updateTaskAssignees(
      organization.id, task.id, assigneeDelta(task.assignees.map(person => person.id), assigneeIds),
    );
    storeSavedTask(saved, task);
  });

  const toggleTaskWatch = task => perform(async () => {
    const watch = await tasksService.setWatch(organization.id, task.id, !task.is_watched_by_me);
    setTasks(current => current.map(item => item.id === task.id ? { ...item, ...watch } : item));
  });

  const copyTaskLink = task => perform(async () => {
    const link = `${window.location.origin}/organization/tasks/${organization.id}?task=${task.id}`;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
    } else {
      const input = document.createElement('textarea');
      input.value = link;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (!copied) throw new Error('Nie udało się skopiować linku');
    }
    setCopiedTaskId(task.id);
  });

  const moveTask = async (taskId, status) => {
    const task = tasks.find(item => item.id === taskId);
    setDraggedId(null); setDropTarget(null);
    if (!task || task.status === status || movingId) return;
    const previousStatus = task.status;
    const previousStatusEnteredAt = task.status_entered_at;
    setMovingId(taskId); setError('');
    setTasks(current => current.map(item => item.id === taskId ? { ...item, status, status_entered_at: new Date().toISOString() } : item));
    setTotals(current => ({
      ...current,
      [previousStatus]: Math.max(0, current[previousStatus] - 1),
      [status]: current[status] + 1,
    }));
    try {
      const saved = await tasksService.updateTaskStatus(organization.id, taskId, status);
      setTasks(current => current.map(item => item.id === taskId ? saved : item));
    } catch (error) {
      setTasks(current => current.map(item => item.id === taskId ? { ...item, status: previousStatus, status_entered_at: previousStatusEnteredAt } : item));
      setTotals(current => ({
        ...current,
        [previousStatus]: current[previousStatus] + 1,
        [status]: Math.max(0, current[status] - 1),
      }));
      setError(errorMessage(error));
      if ([403, 404].includes(error.response?.status)) onAccessLost();
    } finally { setMovingId(null); }
  };

  const today = new Date().toLocaleDateString('sv-SE');
  const commentLength = serializeMentions(commentContent, commentMentions).length;
  const isOwner = organization.owner_id === userId;
  const normalizedMemberSearch = memberSearch.trim().toLocaleLowerCase('pl');
  const filteredPeople = people?.filter(person =>
    person.full_name.toLocaleLowerCase('pl').includes(normalizedMemberSearch),
  ) || [];

  return <section className="tp-workspace">
    <div className="tp-board-header">
      <div>
        <button className="tp-back" onClick={() => navigate('/organization/tasks')}><ArrowLeft size={17} /> Projekty</button>
        <h3>{organization.name}</h3>
        <span>{organization.is_personal ? 'Projekt osobisty' : `${organization.members.length} członków`}</span>
      </div>
      <div className="tp-actions">
        <button className="tp-primary" disabled={busy || loading} onClick={() => openEditor()}><Plus size={17} /> Nowe zadanie</button>
        {!organization.is_personal && <div className="tp-members-control" ref={membersRef}>
          <button type="button" disabled={busy} aria-expanded={Boolean(people)} aria-haspopup="dialog" onClick={openMembers}><Users size={17} /> Członkowie</button>
          {people && <form className="tp-members-dropdown" aria-label="Członkowie projektu" onSubmit={event => { event.preventDefault(); perform(async () => {
            const updated = await tasksService.updateMembers(organization.id, memberIds);
            onOrganizationChange(updated); setPeople(null); setMemberSearch(''); setReload(value => value + 1);
          }); }}>
            <div className="tp-members-heading"><div><strong>Członkowie projektu</strong><small>{organization.members.length} obecnych</small></div>
              <button type="button" className="tp-close" aria-label="Zamknij listę członków" onClick={() => { setPeople(null); setMemberSearch(''); }}><X size={17} /></button></div>
            <label className="tp-members-search"><Search size={16} /><input autoFocus aria-label="Szukaj użytkownika" placeholder="Szukaj użytkownika…"
              value={memberSearch} onChange={event => setMemberSearch(event.target.value)} /></label>
            <p>{isOwner ? 'Dodawaj lub usuwaj członków.' : 'Możesz zapraszać nowe osoby. Tylko właściciel może usuwać członków.'}</p>
            <PeoplePicker people={filteredPeople} selected={memberIds} onChange={setMemberIds} ownerId={organization.owner_id}
              lockedIds={isOwner ? [] : organization.members.map(person => person.id)} disabled={busy} />
            {!filteredPeople.length && <p className="tp-members-empty">Nie znaleziono użytkownika.</p>}
            <div className="tp-members-actions"><button type="submit" className="tp-primary" disabled={busy}>Zapisz</button></div>
          </form>}
        </div>}
        {!organization.is_personal && <button disabled={auditLoading} onClick={() => loadAudit()}><History size={17} /> Dziennik</button>}
        <button type="button" className={archiveOpen ? 'tp-archive-toggle-active' : ''} aria-pressed={archiveOpen}
          onClick={toggleArchiveView}><Archive size={17} /> Archiwum zadań ({archiveTotal})</button>
        <button disabled={busy || loading} onClick={() => { setReload(value => value + 1); setArchiveReload(value => value + 1); }}>Odśwież</button>
      </div>
    </div>
    {error && <p className="tp-error" role="alert">{error}</p>}
    {editor && !editor.id && <div className="tp-window-backdrop" role="presentation" onMouseDown={() => { if (!busy) setEditor(null); }}>
      <form className="tp-task-modal" role="dialog" aria-modal="true" aria-labelledby="tp-new-task-title" onSubmit={saveTask} onMouseDown={event => event.stopPropagation()}>
        <div className="tp-window-header"><div><span>Nowe zadanie</span><h4 id="tp-new-task-title">Utwórz zadanie</h4></div><button type="button" className="tp-close" aria-label="Zamknij" disabled={busy} onClick={() => setEditor(null)}><X /></button></div>
        {error && <p className="tp-error" role="alert">{error}</p>}
        <fieldset disabled={busy}>
          <label>Tytuł<input autoFocus required maxLength={200} value={editor.title} onChange={event => setEditor({ ...editor, title: event.target.value })} /></label>
          <label>Opis<textarea rows={5} maxLength={20000} value={editor.description} onChange={event => setEditor({ ...editor, description: event.target.value })} /></label>
          <TaskAttachmentPicker files={editor.files || []} onChange={files => setEditor({ ...editor, files })} disabled={busy} />
          <div className="tp-fields">
            <label>Termin wykonania<input type="date" value={editor.deadline} onChange={event => setEditor({ ...editor, deadline: event.target.value })} /></label>
            <label>Priorytet<select value={editor.priority} onChange={event => setEditor({ ...editor, priority: event.target.value })}>{Object.entries(priorities).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label>Status<select value={editor.status} onChange={event => setEditor({ ...editor, status: event.target.value })}>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <div><h4>Lista kontrolna</h4><ChecklistEditor items={editor.checklist_items} onChange={items => setEditor({ ...editor, checklist_items: items })} disabled={busy} /></div>
          <div><h4>Przypisane osoby</h4><PeoplePicker people={organization.members} selected={editor.assignee_ids} onChange={ids => setEditor({ ...editor, assignee_ids: ids })} /></div>
          <div className="tp-window-actions"><button type="button" onClick={() => setEditor(null)}>Anuluj</button><button className="tp-primary" disabled={!editor.title.trim()}>{busy ? 'Zapisywanie…' : 'Utwórz zadanie'}</button></div>
        </fieldset>
      </form>
    </div>}
    {editor?.id && <div className="tp-drawer-backdrop" role="presentation" onMouseDown={() => { if (!busy && !commentSubmitting && deletingCommentId === null) { setEditor(null); setEditingExisting(false); } }}>
      <aside className="tp-task-drawer" role="dialog" aria-modal="true" aria-labelledby="tp-task-drawer-title" onMouseDown={event => event.stopPropagation()}>
        <div className="tp-window-header"><div><span className="tp-task-id">#{editor.id}</span><h4 id="tp-task-drawer-title">Szczegóły zadania</h4></div><button type="button" className="tp-close" aria-label="Zamknij" disabled={busy || commentSubmitting || deletingCommentId !== null} onClick={() => { setEditor(null); setEditingExisting(false); }}><X /></button></div>
        <div className="tp-drawer-tabs" role="tablist" aria-label="Sekcje zadania">
          <button type="button" role="tab" aria-selected={drawerTab === 'details'} className={drawerTab === 'details' ? 'tp-drawer-tab-active' : ''}
            onClick={() => setDrawerTab('details')}>Szczegóły</button>
          <button type="button" role="tab" aria-selected={drawerTab === 'comments'} className={drawerTab === 'comments' ? 'tp-drawer-tab-active' : ''}
            disabled={editingExisting} onClick={openComments}><MessageSquare size={16} /> Komentarze ({editor.comment_count})</button>
          <button type="button" role="tab" aria-selected={drawerTab === 'attachments'} className={drawerTab === 'attachments' ? 'tp-drawer-tab-active' : ''}
            disabled={editingExisting} onClick={() => setDrawerTab('attachments')}><Paperclip size={16} /> Załączniki ({(editor.attachments || []).length})</button>
        </div>
        {editor.archived_at && <div className="tp-archived-banner" role="note">
          <Archive size={17} />
          <span><strong>Zadanie w archiwum</strong> · przeniesione {new Date(editor.archived_at).toLocaleDateString('pl-PL')}{editor.archived_by ? ` przez ${editor.archived_by.full_name}` : ''}. Status: {statuses[editor.status]}.</span>
        </div>}
        {drawerTab === 'comments' ? <section className="tp-comments" role="tabpanel">
          {editor.archived_at ? <div className="tp-archived-note">
            {error && <p className="tp-error" role="alert">{error}</p>}
            <p>Zadanie jest w archiwum. Przywróć je, aby dodawać komentarze.</p>
          </div> : <form className="tp-comment-form" onSubmit={addComment}>
            {error && <p className="tp-error" role="alert">{error}</p>}
            <label htmlFor="tp-new-comment">Dodaj komentarz</label>
            <MentionTextarea id="tp-new-comment" rows={4} maxLength={5000} value={commentContent} onChange={setCommentContent}
              placeholder={organization.is_personal ? 'Napisz komentarz…' : 'Napisz komentarz… Wpisz @, aby oznaczyć osobę z projektu.'}
              mentions={commentMentions} onMentionsChange={setCommentMentions} people={organization.members} currentUserId={userId}
              renderAvatar={person => <PersonAvatar person={person} />}
              disabled={commentSubmitting || commentsLoading || deletingCommentId !== null} />
            <TaskAttachmentPicker files={commentFiles} onChange={setCommentFiles} disabled={commentSubmitting} />
            <div className="tp-comment-form-actions"><small className={commentLength > 5000 ? 'tp-comment-too-long' : ''}>{commentLength}/5000</small><button className="tp-primary" disabled={commentSubmitting || commentsLoading || deletingCommentId !== null || !commentContent.trim() || commentLength > 5000}><Send size={16} /> {commentSubmitting ? 'Dodawanie…' : 'Dodaj komentarz'}</button></div>
          </form>}
          <div className="tp-comments-list" aria-live="polite">
            {comments.map(comment => <article key={comment.id} className="tp-comment">
              <PersonAvatar person={comment.author} />
              <div><header><span><strong>{comment.author.full_name}</strong><time dateTime={comment.created_at}>{new Date(comment.created_at).toLocaleString('pl-PL')}</time></span>
                {(comment.author_id === userId || organization.owner_id === userId) && <button type="button" className="tp-comment-delete" aria-label={`Usuń komentarz użytkownika ${comment.author.full_name}`}
                  title="Usuń komentarz" disabled={deletingCommentId !== null} onClick={() => deleteComment(comment)}><Trash2 size={15} /></button>}
              </header><p><CommentContent content={comment.content} currentUserId={userId} /></p><TaskAttachmentList attachments={comment.attachments} organizationId={organization.id} taskId={editor.id} /></div>
            </article>)}
            {commentsLoading && !comments.length && <p className="tp-comments-empty" role="status">Ładowanie komentarzy…</p>}
            {!commentsLoading && commentsLoaded && !comments.length && <p className="tp-comments-empty">To zadanie nie ma jeszcze komentarzy.</p>}
          </div>
          {comments.length < commentTotal && <button className="tp-load-more" disabled={commentsLoading} onClick={() => loadComments(comments.length)}>{commentsLoading ? 'Ładowanie…' : `Załaduj wcześniejsze (${commentTotal - comments.length})`}</button>}
        </section> : drawerTab === 'attachments' ? <section className="tp-task-attachments" role="tabpanel">
          <TaskAttachmentList attachments={editor.attachments} organizationId={organization.id} taskId={editor.id} />
          {!(editor.attachments || []).length && <p className="tp-attachments-empty">To zadanie nie ma załączników.</p>}
        </section> : editingExisting ? <form className="tp-drawer-form" onSubmit={saveTask}>
          {error && <p className="tp-error" role="alert">{error}</p>}
          {conflict && <div className="tp-conflict">
            <p>Możesz wczytać aktualną wersję zadania (Twoje zmiany zostaną odrzucone) albo zapisać swoje zmiany i nadpisać zmiany tej osoby.</p>
            <div className="tp-window-actions">
              <button type="button" disabled={busy} onClick={cancelExistingEdit}>Wczytaj aktualną wersję</button>
              <button type="button" className="tp-danger" disabled={busy} onClick={() => submitTask({ ...editor, version: conflict.version })}>Nadpisz moimi zmianami</button>
            </div>
          </div>}
          <fieldset disabled={busy}>
            <label>Tytuł<input autoFocus required maxLength={200} value={editor.title} onChange={event => setEditor({ ...editor, title: event.target.value })} /></label>
            <label>Opis<textarea rows={7} maxLength={20000} value={editor.description} onChange={event => setEditor({ ...editor, description: event.target.value })} /></label>
            <TaskAttachmentPicker files={editor.files || []} onChange={files => setEditor({ ...editor, files })} disabled={busy} />
            <div className="tp-drawer-fields">
              <label>Termin wykonania<input type="date" value={editor.deadline} onChange={event => setEditor({ ...editor, deadline: event.target.value })} /></label>
              <label>Priorytet<select value={editor.priority} onChange={event => setEditor({ ...editor, priority: event.target.value })}>{Object.entries(priorities).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>Status<select value={editor.status} onChange={event => setEditor({ ...editor, status: event.target.value })}>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            </div>
            <div><h4>Lista kontrolna</h4><ChecklistEditor items={editor.checklist_items} onChange={items => setEditor({ ...editor, checklist_items: items })} disabled={busy} /></div>
            <div><h4>Przypisane osoby</h4><PeoplePicker people={organization.members} selected={editor.assignee_ids} onChange={ids => setEditor({ ...editor, assignee_ids: ids })} /></div>
            <div className="tp-window-actions"><button type="button" onClick={cancelExistingEdit}>Anuluj</button><button className="tp-primary" disabled={!editor.title.trim()}>{busy ? 'Zapisywanie…' : 'Zapisz zmiany'}</button></div>
          </fieldset>
        </form> : <div className="tp-task-details" role="tabpanel">
          <div className="tp-detail-heading"><h3>{editor.title}</h3><span className={`tp-priority tp-priority-${editor.priority}`}>{priorities[editor.priority]}</span></div>
          <div className="tp-detail-row"><span>Status</span><strong>{statuses[editor.status]}</strong></div>
          <div className="tp-detail-row"><span>Termin wykonania</span><strong>{editor.deadline ? editor.deadline.split('-').reverse().join('.') : 'Bez terminu'}</strong></div>
          <div className="tp-detail-section"><span>Opis</span><p>{editor.description || 'Brak opisu'}</p></div>
          <div className="tp-detail-section"><span>Lista kontrolna</span><div className="tp-checklist-view">{editor.checklist_items.map((item, index) => <label key={item.id || index}>
            <input type="checkbox" checked={item.is_completed} disabled={busy || Boolean(editor.archived_at)} onChange={() => toggleDrawerChecklistItem(item)} />
            <span className={item.is_completed ? 'tp-checklist-done' : ''}>{item.text}</span>
          </label>)}{!editor.checklist_items.length && <p>Brak elementów listy.</p>}</div></div>
          <div className="tp-detail-section"><span>Przypisane osoby</span><p className="tp-inline-hint">{editor.archived_at ? 'Zadanie z archiwum jest tylko do odczytu.' : 'Możesz zmienić przypisanie niezależnie od statusu zadania.'}</p><PeoplePicker people={organization.members} selected={editor.assignee_ids} onChange={updateDrawerAssignees} disabled={busy || Boolean(editor.archived_at)} /></div>
          {error && <p className="tp-error" role="alert">{error}</p>}
          <div className="tp-window-actions">{editor.archived_at
            ? <button className="tp-primary" disabled={busy} onClick={() => setTaskArchived(editor, false)}><ArchiveRestore size={16} /> Przywróć z archiwum</button>
            : <><button type="button" disabled={busy} onClick={() => setTaskArchived(editor, true)}><Archive size={16} /> Przenieś do archiwum</button>
              <button className="tp-primary" onClick={startExistingEdit}><Pencil size={16} /> Edytuj zadanie</button></>}</div>
        </div>}
      </aside>
    </div>}
    {deleteCandidate && <div className="tp-window-backdrop tp-confirm-backdrop" role="presentation" onMouseDown={() => { if (!busy) setDeleteCandidate(null); }}>
      <div className="tp-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="tp-delete-title" aria-describedby="tp-delete-description" onMouseDown={event => event.stopPropagation()}>
        <span className="tp-confirm-icon"><AlertTriangle /></span>
        <h4 id="tp-delete-title">Usunąć zadanie?</h4>
        <p id="tp-delete-description">Czy na pewno chcesz trwale usunąć zadanie <strong>„{deleteCandidate.title}”</strong>?</p>
        <p className="tp-confirm-note">Zadanie, komentarze i ich załączniki zostaną usunięte. Tej operacji nie można cofnąć.</p>
        <div className="tp-window-actions"><button disabled={busy} onClick={() => setDeleteCandidate(null)}>Anuluj</button><button className="tp-danger" disabled={busy} onClick={deleteTask}><Trash2 size={16} /> {busy ? 'Usuwanie…' : 'Usuń trwale'}</button></div>
      </div>
    </div>}
    {auditOpen && <div className="tp-window-backdrop" role="presentation" onMouseDown={() => setAuditOpen(false)}>
      <section className="tp-audit-modal" role="dialog" aria-modal="true" aria-labelledby="tp-audit-title" onMouseDown={event => event.stopPropagation()}>
        <div className="tp-window-header"><div><span>Projekt: {organization.name}</span><h4 id="tp-audit-title">Dziennik operacji</h4></div><button type="button" className="tp-close" aria-label="Zamknij" onClick={() => setAuditOpen(false)}><X /></button></div>
        <div className="tp-audit-list">
          {auditItems.map(entry => <article key={entry.id} className="tp-audit-entry">
            <div><strong>{auditLabels[entry.action] || entry.action}</strong><time dateTime={entry.created_at}>{new Date(entry.created_at).toLocaleString('pl-PL')}</time></div>
            <p><b>{entry.actor_name}</b>{entry.task_id ? ` · #${entry.task_id} ${entry.task_title || ''}` : ''}</p>
            {auditDetails(entry) && <small>{auditDetails(entry)}</small>}
          </article>)}
          {!auditItems.length && !auditLoading && <p className="tp-audit-empty">Brak zapisanych operacji.</p>}
        </div>
        {auditItems.length < auditTotal && <button className="tp-load-more" disabled={auditLoading} onClick={() => loadAudit(auditItems.length)}>{auditLoading ? 'Ładowanie…' : `Załaduj wcześniejsze (${auditTotal - auditItems.length})`}</button>}
      </section>
    </div>}
    {archiveOpen ? <TaskArchive organizationId={organization.id} service={tasksService} statuses={statuses} priorities={priorities}
      reloadKey={archiveReload} busy={busy} onOpen={openEditor} onRestore={task => setTaskArchived(task, false)} onBack={toggleArchiveView}
      renderPeople={people => <AvatarStack people={people} />} errorMessage={errorMessage} onAccessLost={onAccessLost} /> : <>
    <div className="tp-filters">
      <input aria-label="Szukaj zadań" placeholder="Szukaj po tytule lub opisie…" value={search} onChange={event => setSearch(event.target.value)} />
      <label><input type="checkbox" checked={onlyMine} onChange={event => setOnlyMine(event.target.checked)} /> Tylko przypisane do mnie</label>
    </div>
    {loading ? <p role="status">Ładowanie zadań…</p> : <div className="tp-kanban" aria-label="Tablica zadań">
      {columns.map(column => {
        const columnTasks = tasks.filter(task => task.status === column.value).sort(sortColumnTasks);
        return <section key={column.value} data-status={column.value} className={`tp-column tp-column-${column.value} ${dropTarget === column.value ? 'tp-column-drop' : ''}`}
          onDragOver={event => { event.preventDefault(); setDropTarget(column.value); }}
          onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDropTarget(null); }}
          onDrop={event => { event.preventDefault(); moveTask(Number(event.dataTransfer.getData('text/plain') || draggedId), column.value); }}>
          <header><span className="tp-column-dot" /> <strong>{column.label}</strong><span>({totals[column.value]})</span></header>
          {column.value === 'todo' && <button className="tp-column-add" onClick={() => setEditor(blankTask('todo'))}><Plus size={17} /> Nowe zadanie</button>}
          <div className="tp-column-tasks">
            {columnTasks.map(task => <TaskCard key={task.id} task={task} members={organization.members} busy={busy || movingId === task.id} copied={copiedTaskId === task.id} today={today} onOpen={openEditor} onAssign={updateCardAssignees} onCopyLink={copyTaskLink} onToggleWatch={toggleTaskWatch} onRequestDelete={setDeleteCandidate}
              onDragStart={(event, draggedTask) => { setDraggedId(draggedTask.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(draggedTask.id)); }}
              onDragEnd={() => { setDraggedId(null); setDropTarget(null); }} />)}
            {!columnTasks.length && <div className="tp-empty-column">Przeciągnij zadanie tutaj</div>}
            {columnTasks.length < totals[column.value] && <button className="tp-load-more" disabled={loadingMore !== null} onClick={() => loadMore(column.value)}>{loadingMore === column.value ? 'Ładowanie…' : `Załaduj więcej (${totals[column.value] - columnTasks.length})`}</button>}
          </div>
        </section>;
      })}
    </div>}
    </>}
  </section>;
}

export default function TasksProjects() {
  const { user } = useAuth();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [personal, setPersonal] = useState(false);
  const [deleteOrganizationCandidate, setDeleteOrganizationCandidate] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    tasksService.organizations().then(data => {
      if (active) { setOrganizations(data); setError(''); }
    }).catch(error => { if (active) { setOrganizations([]); setError(errorMessage(error)); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);

  const [handleAccessLost] = useState(() => () => {
    setOrganizations([]); navigate('/organization/tasks', { replace: true }); setReload(value => value + 1);
  });
  const selected = organizations.find(item => String(item.id) === organizationId);

  async function create(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const organization = await tasksService.createOrganization({ name, is_personal: personal });
      setOrganizations(previous => [...previous, organization]); setCreating(false); setName(''); setPersonal(false);
      navigate(`/organization/tasks/${organization.id}`);
    } catch (error) { setError(errorMessage(error)); } finally { setBusy(false); }
  }

  async function deleteOrganization() {
    setBusy(true); setError('');
    try {
      await tasksService.deleteOrganization(deleteOrganizationCandidate.id);
      setOrganizations(previous => previous.filter(organization => organization.id !== deleteOrganizationCandidate.id));
      setDeleteOrganizationCandidate(null);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return <div className={organizationId ? 'tp-page tp-page-board' : 'page-container tp-page'}>
    <div className="page-header"><h2 className="module-page-title"><ListTodo aria-hidden="true" /> Zadania i projekty</h2><p>Planuj pracę zespołu i własne zadania.</p></div>
    <div className="page-content">
      {error && <p className="tp-error" role="alert">{error} <button onClick={() => setReload(value => value + 1)}>Ponów</button></p>}
      {loading ? <p role="status">Ładowanie projektów…</p> : organizationId ? (
        selected
          ? <Workspace key={selected.id} organization={selected} userId={user?.id} onAccessLost={handleAccessLost} onOrganizationChange={updated => setOrganizations(previous => previous.map(item => item.id === updated.id ? updated : item))} />
          : <div className="tp-empty-state"><LockKeyhole /><h3>Brak dostępu do projektu</h3><button onClick={() => navigate('/organization/tasks')}>Wróć do listy</button></div>
      ) : <>
        <div className="tp-list-header"><div><h3>Dostępne projekty</h3><p>Wybierz projekt, aby otworzyć jego tablicę zadań.</p></div><button className="tp-primary" disabled={busy} onClick={() => setCreating(!creating)}><Plus size={17} /> Nowy projekt</button></div>
        {creating && <form className="tp-panel" onSubmit={create}><h3>Nowy projekt</h3><fieldset disabled={busy}>
          <label>Nazwa<input autoFocus required maxLength={150} placeholder="np. AI" value={name} onChange={event => setName(event.target.value)} /></label>
          <label className="tp-checkbox"><input type="checkbox" checked={personal} onChange={event => setPersonal(event.target.checked)} /> Osobista — dostępna tylko dla mnie</label>
          <div className="tp-actions"><button className="tp-primary" disabled={!name.trim()}>{busy ? 'Tworzenie…' : 'Utwórz'}</button><button type="button" onClick={() => setCreating(false)}>Anuluj</button></div>
        </fieldset></form>}
        <div className="tp-organizations">
          {organizations.map(organization => <OrganizationCard key={organization.id} organization={organization} userId={user?.id}
            onOpen={item => navigate(`/organization/tasks/${item.id}`)} onDelete={setDeleteOrganizationCandidate} />)}
          {!organizations.length && <div className="tp-empty-state tp-organizations-empty"><Building2 /><p>Aktualnie nie jesteś przypisany do żadnego projektu. Możesz założyć własny projekt lub poprosić właściciela zespołu o dodanie.</p></div>}
        </div>
      </>}
    </div>
    {deleteOrganizationCandidate && <div className="tp-window-backdrop tp-confirm-backdrop" role="presentation" onMouseDown={() => { if (!busy) setDeleteOrganizationCandidate(null); }}>
      <div className="tp-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="tp-delete-organization-title" aria-describedby="tp-delete-organization-description" onMouseDown={event => event.stopPropagation()}>
        <span className="tp-confirm-icon"><AlertTriangle /></span>
        <h4 id="tp-delete-organization-title">Usunąć projekt?</h4>
        <p id="tp-delete-organization-description">Czy na pewno chcesz trwale usunąć projekt <strong>„{deleteOrganizationCandidate.name}”</strong> wraz ze wszystkimi jego zadaniami?</p>
        <p className="tp-confirm-note">Zadania, komentarze i ich załączniki zostaną usunięte. Tej operacji nie można cofnąć.</p>
        <div className="tp-window-actions"><button disabled={busy} onClick={() => setDeleteOrganizationCandidate(null)}>Anuluj</button><button className="tp-danger" disabled={busy} onClick={deleteOrganization}><Trash2 size={16} /> {busy ? 'Usuwanie…' : 'Usuń projekt'}</button></div>
      </div>
    </div>}
  </div>;
}
